import React from 'react';

interface CohortData {
    cohortWeek: string;
    userCount: number;
    retentionByWeek: Record<number, number>;
}

interface CohortRetentionGridProps {
    data: CohortData[];
    title: string;
    subtitle?: string;
    maxWeeksOut?: number;
}

function getRetentionColor(pct: number): string {
    if (pct >= 80) return 'bg-green-600 text-white';
    if (pct >= 60) return 'bg-green-500 text-white';
    if (pct >= 40) return 'bg-green-400 text-white';
    if (pct >= 20) return 'bg-green-300 text-green-900';
    if (pct > 0) return 'bg-green-200 text-green-900';
    return 'bg-muted/30 text-muted-foreground';
}

export const CohortRetentionGrid: React.FC<CohortRetentionGridProps> = ({
    data,
    title,
    subtitle,
    maxWeeksOut = 12,
}) => {
    if (!data || data.length === 0) {
        return (
            <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
                <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground">No data available</p>
            </div>
        );
    }

    const weeks = Array.from({ length: maxWeeksOut }, (_, i) => i + 1);

    return (
        <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
            <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
            {subtitle && <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>}
            <div className="overflow-x-auto mt-4">
                <table className="w-full text-xs">
                    <thead>
                        <tr>
                            <th className="text-left p-2 text-muted-foreground font-medium">Cohort</th>
                            <th className="text-center p-2 text-muted-foreground font-medium">Users</th>
                            {weeks.map(w => (
                                <th key={w} className="text-center p-2 text-muted-foreground font-medium">
                                    W{w}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((cohort) => (
                            <tr key={cohort.cohortWeek} className="border-t border-border/30">
                                <td className="p-2 text-muted-foreground whitespace-nowrap">
                                    {cohort.cohortWeek}
                                </td>
                                <td className="p-2 text-center font-medium text-foreground">
                                    {cohort.userCount}
                                </td>
                                {weeks.map(w => {
                                    const pct = cohort.retentionByWeek[w];
                                    if (pct === undefined) {
                                        return <td key={w} className="p-1"><div className="w-full h-8 rounded bg-muted/10" /></td>;
                                    }
                                    return (
                                        <td key={w} className="p-1">
                                            <div
                                                className={`w-full h-8 rounded flex items-center justify-center text-xs font-medium ${getRetentionColor(pct)}`}
                                                title={`${cohort.cohortWeek} - Week ${w}: ${pct}%`}
                                            >
                                                {pct}%
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
